import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

/**
 * Varsel-popup ved «Avslutt plukk» styrt av leveringsmåten (kundeønske Strømmes).
 *
 * Vises når minst én ordre i plukklisten har en leveringsmåte med
 * PickCompletionWarningYesNo = true (f.eks. «B2B Bedriftspakke Ekspress»).
 * Operatøren MÅ bekrefte med OK før avslutningen fortsetter — hensikten er at
 * ekspress-sendinger o.l. ikke legges sammen med vanlig post.
 *
 * Varslingsteksten (PickCompletionWarningText) er fritekst per leveringsmåte,
 * vedlikeholdt i Leveringsmåter-administrasjonen. Tom tekst → generisk fallback.
 */
export interface PickCompletionWarning {
  orderNo: number;
  deliveryMethodNo: number;
  deliveryMethodDescription: string | null;
  warningText: string | null;
}

interface PickCompletionWarningModalProps {
  isOpen: boolean;
  warnings: PickCompletionWarning[];
  /** OK — operatøren har bekreftet, avslutningen fortsetter */
  onConfirm: () => void;
  /** Avbryt — tilbake til plukket uten å avslutte */
  onCancel: () => void;
}

export const PickCompletionWarningModal: React.FC<PickCompletionWarningModalProps> = ({
  isOpen,
  warnings,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  if (!isOpen || warnings.length === 0) return null;

  // Én rad per unik (leveringsmåte, tekst) — men behold ordrenumrene for sporbarhet
  const grouped = new Map<number, { warning: PickCompletionWarning; orderNos: number[] }>();
  warnings.forEach(w => {
    const existing = grouped.get(w.deliveryMethodNo);
    if (existing) {
      existing.orderNos.push(w.orderNo);
    } else {
      grouped.set(w.deliveryMethodNo, { warning: w, orderNos: [w.orderNo] });
    }
  });

  return (
    // z-[60]: må ligge OVER DeliveryInfoModal (z-50) — varselet vises mens
    // leveranseinformasjonen fortsatt står åpen under
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={26} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('warehouse.modules.picking.completionWarning.title', {}, 'Obs — sjekk leveringsmåte')}
            </h3>
            <div className="space-y-3">
              {Array.from(grouped.values()).map(({ warning, orderNos }) => (
                <div
                  key={warning.deliveryMethodNo}
                  className="border border-amber-300 bg-amber-50 rounded-lg p-3"
                >
                  <p className="text-base font-semibold text-amber-900">
                    {warning.warningText?.trim() ||
                      t('warehouse.modules.picking.completionWarning.defaultText', {},
                        'Denne leveringsmåten krever ekstra oppmerksomhet.')}
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    {warning.deliveryMethodDescription || `${t('warehouse.modules.picking.completionWarning.deliveryMethod', {}, 'Leveringsmåte')} ${warning.deliveryMethodNo}`}
                    {' — '}
                    {orderNos.length === 1
                      ? t('warehouse.modules.picking.completionWarning.orderSingle', { '0': String(orderNos[0]) }, `ordre ${orderNos[0]}`)
                      : t('warehouse.modules.picking.completionWarning.orderMultiple', { '0': orderNos.join(', ') }, `ordrene ${orderNos.join(', ')}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 order-2 sm:order-1"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            className="px-6 py-2 min-h-[44px] bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 order-1 sm:order-2"
          >
            {t('common.buttons.ok', {}, 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
};
